/**
 * "Import aircraft" on the Aircraft page (MB's R79; help `cloud.aircraft`; docs/specs/cloud/
 * tjr-register-import.md). The editor chooses the list file `tjf.dat` and any project files (`Report_Log_*.tjf`); Teslyn
 * reads them in the browser, looks the registrations up in the national register, matches them with the aircraft
 * register by registration and shows what would be added. Ticked by default: every aircraft not in the register
 * (decision 547), with "Type unknown" where nothing gives its type.
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AIRFRAME_LABEL,
  TjrFileError,
  aircraftImportInserts,
  planAircraftImport,
  readTjrContainer,
  readTjrDefaults,
  readTjrJobAircraft,
  type Aircraft,
  type AircraftImportRow,
  type TjrDefaults,
  type TjrJobAircraft,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { fileBytes } from '../components/fileBytes.ts';
import { importAircraft, lookupRegisterTypes } from './api.ts';

export interface AircraftImportPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly aircraft: readonly Aircraft[];
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}

const SOURCE_WORD: Readonly<Record<AircraftImportRow['typeSource'], string>> = {
  tjr_job: 'Project file',
  register: 'National register',
  none: '–',
};

export function AircraftImportPanel({
  sb,
  tenantId,
  tenantName,
  aircraft,
  onDone,
  onCancel,
}: AircraftImportPanelProps): ReactElement {
  const [rows, setRows] = useState<AircraftImportRow[] | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function choose(chosen: readonly File[]): Promise<void> {
    if (chosen.length === 0) return;
    setError(null);
    setRows(null);
    setBusy(true);
    try {
      let defaults: TjrDefaults | null = null;
      const jobs: TjrJobAircraft[] = [];
      for (const f of chosen) {
        const bytes = await fileBytes(f);
        const isJob = readTjrContainer(bytes).some((r) => r.code === '013');
        if (isJob) jobs.push(...readTjrJobAircraft(bytes));
        else defaults = readTjrDefaults(bytes);
      }
      const marks = [
        ...new Set([
          ...(defaults?.registrations ?? []).map((r) => r.registration),
          ...jobs.map((j) => j.registration),
        ]),
      ];
      const lookup = await lookupRegisterTypes(sb, tenantId, tenantName, marks);
      setFiles(chosen.map((f) => f.name));
      setRows(planAircraftImport(defaults, jobs, aircraft, lookup));
    } catch (e) {
      setError(e instanceof TjrFileError ? e.message : `The files could not be read: ${String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function apply(): Promise<void> {
    if (!rows) return;
    setBusy(true);
    setError(null);
    try {
      const added = await importAircraft(sb, aircraftImportInserts(rows, tenantId));
      onDone(added > 0 ? `Added ${added} aircraft.` : 'Nothing was imported.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const ticked = rows?.filter((r) => r.selected && r.status === 'new') ?? [];
  const toggle = (reg: string, on: boolean): void =>
    setRows((list) => (list ?? []).map((r) => (r.registration === reg ? { ...r, selected: on } : r)));

  return (
    <section className="panel stack" aria-label="Import aircraft">
      <h2 className="heading">Import aircraft</h2>
      <p className="body">
        Choose the staff and aircraft list file (<span className="data">tjf.dat</span>, in the previous
        reporting program&apos;s folder) and, for the aircraft types, any project files (
        <span className="data">Report_Log_*.tjf</span>). The list file keeps a registration without its type;
        Teslyn takes the type from a project file that flew the aircraft, else from the national register when
        that register&apos;s aircraft is the one the list describes. An aircraft already in the register (the
        same registration) is never added twice.
      </p>
      <label className="field">
        <span className="label">List and project files (tjf.dat, Report_Log_*.tjf)</span>
        <input
          type="file"
          multiple
          accept=".dat,.tjf"
          onChange={(e) => {
            const chosen = Array.from(e.target.files ?? []);
            e.target.value = '';
            void choose(chosen);
          }}
        />
      </label>
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {rows && (
        <>
          <p className="body" role="status">
            {files.join(', ')}: {rows.length} registrations, {rows.filter((r) => r.status === 'new').length}{' '}
            not in the register. Every aircraft not in the register is ticked; one with no type is added as
            &quot;Type unknown&quot;, to fill in later on this page.
          </p>
          <div className="table-wrap">
            <table className="table" aria-label="Aircraft to import">
              <thead>
                <tr>
                  <th className="label">
                    <span className="sr-only">Import</span>
                  </th>
                  <th className="label">Registration</th>
                  <th className="label">Type</th>
                  <th className="label">Type from</th>
                  <th className="label">What happens</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.registration}>
                    <td>
                      {r.status === 'new' && (
                        <input
                          type="checkbox"
                          aria-label={`Import ${r.registration}`}
                          checked={r.selected}
                          onChange={(e) => toggle(r.registration, e.target.checked)}
                        />
                      )}
                    </td>
                    <td className="data">{r.registration}</td>
                    <td>
                      {r.typeName || 'Type unknown'}
                      {r.airframe && <span className="caption muted"> ({AIRFRAME_LABEL[r.airframe]})</span>}
                    </td>
                    <td>{SOURCE_WORD[r.typeSource]}</td>
                    <td>
                      {r.status === 'new' ? 'Add' : 'Already in the register'}
                      {r.note && <span className="caption muted"> · {r.note}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="row">
        <Button variant="primary" busy={busy} disabled={ticked.length === 0} onClick={() => void apply()}>
          {`Import ${ticked.length} aircraft`}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
